import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useReportCatalog } from 'api/reports';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';
import { localizeReport } from 'i18n/metadata';

// Local chip: report/metric statuses (available | preparing | locked) are not
// the pipeline/connection statuses that components/common/StatusBadge renders.
function StatusChip({ status }) {
  const { t } = useTranslation();
  if (status === 'available') {
    return <Chip label={t('data.available')} color="success" size="small" />;
  }
  if (status === 'preparing') {
    return (
      <Chip
        label={t('data.preparing')}
        color="info"
        size="small"
        variant="outlined"
        icon={<CircularProgress size={12} color="info" />}
      />
    );
  }
  return (
    <Chip
      label={t('data.locked')}
      size="small"
      variant="outlined"
      icon={<LockOutlinedIcon />}
      sx={{ color: 'text.secondary', '& .MuiChip-icon': { color: 'text.disabled' } }}
    />
  );
}

function joinNames(sources) {
  const names = (sources || []).map((s) => s.name);
  if (names.length <= 1) return names[0] || '';
  return t('data.valueAndValue', { value1: names.slice(0, -1).join(', '), value2: names[names.length - 1] });
}

// Mirrors the report-level footer so the tooltip never instructs an impossible
// action: "connect" only for connectable sources, "coming soon" for the rest.
function lockedMetricTooltip(sources) {
  const connectable = (sources || []).filter((s) => s.connectable);
  const comingSoon = (sources || []).filter((s) => !s.connectable);
  const parts = [];
  if (connectable.length > 0) {
    parts.push(t('data.connectValueToUnlock', { value1: joinNames(connectable) }));
  }
  if (comingSoon.length > 0) {
    parts.push(t('data.valueComingSoon', { value1: joinNames(comingSoon) }));
  }
  return parts.join('. ');
}

function MetricChip({ metric }) {
  const { t } = useTranslation();
  if (metric.status === 'locked') {
    return (
      <Tooltip title={lockedMetricTooltip(metric.missing_sources)}>
        <Chip
          label={metric.name}
          size="small"
          variant="outlined"
          icon={<LockOutlinedIcon />}
          sx={{
            color: 'text.disabled',
            borderColor: 'divider',
            '& .MuiChip-icon': { color: 'text.disabled' },
          }}
        />
      </Tooltip>
    );
  }
  if (metric.status === 'preparing') {
    return (
      <Tooltip title={t('data.syncingDataThisMetricBecomesAvailableAfterTheNextTransformationRun')}>
        <Chip
          label={metric.name}
          size="small"
          variant="outlined"
          color="info"
          icon={<CircularProgress size={10} color="info" />}
        />
      </Tooltip>
    );
  }
  return <Chip label={metric.name} size="small" variant="outlined" />;
}

function ReportCard({ report, transformationsSupported }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLocked = report.status === 'locked';

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Only the body dims on a locked report; the footer CTAs stay legible */}
        <Box sx={{ opacity: isLocked ? 0.6 : 1, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              {report.name}
            </Typography>
            <StatusChip status={report.status} />
          </Box>

          {report.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {report.description}
            </Typography>
          )}

          {(report.category || report.is_custom) && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              {report.category && (
                <Chip
                  label={report.category_label || report.category}
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: 'capitalize' }}
                />
              )}
              {report.is_custom && (
                <Chip label={t('data.custom')} size="small" variant="outlined" color="secondary" />
              )}
            </Box>
          )}

          {report.metrics?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {report.metrics.map((metric) => (
                <MetricChip key={metric.key} metric={metric} />
              ))}
            </Box>
          )}
        </Box>

        {report.status === 'preparing' && transformationsSupported && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>{t('data.syncingYourDataThisReportBecomesAvailableAfterTheNextTransformation')}</Typography>
        )}

        {/* No connect CTAs on a warehouse that can never build reports; the
            page-level banner explains why. */}
        {isLocked && transformationsSupported && report.missing_sources?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {report.missing_sources.map((source) =>
                source.connectable ? (
                  <Button
                    key={source.slug}
                    size="small"
                    variant="contained"
                    onClick={() =>
                      // A domain no connector is named after (ga4) names the
                      // connector that loads it.
                      navigate(`/console/connectors/new?type=${encodeURIComponent(source.connect_slug || source.slug)}`)
                    }
                  >{t('data.connect')}{' '}{source.name}
                  </Button>
                ) : (
                  <Chip
                    key={source.slug}
                    label={t('data.valueComingSoon', { value1: source.name })}
                    size="small"
                    variant="outlined"
                    disabled
                  />
                )
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useReportCatalog();

  const reports = (data?.reports || []).map(localizeReport);
  const allLocked = reports.length > 0 && reports.every((rep) => rep.status === 'locked');
  const showConnectHint = !data?.warehouse && allLocked;
  // false only when the BI warehouse engine can never run transformations
  // (EL-only target like BigQuery/DuckDB) — no report can materialize there.
  const transformationsSupported = data ? data.transformations_supported !== false : true;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <InsightsIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>{t('data.reports')}</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{t('data.failedToLoadReports')}</Alert>
      )}

      {!error && showConnectHint && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('data.connectADataSourceToStartUnlockingReports')}</Alert>
      )}

      {!error && !transformationsSupported && (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('data.thisWarehouseTypeDoesNotSupportTransformationsSoReportsCannotBe')}</Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !error && reports.length === 0 ? (
        <EmptyState />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 2,
          }}
        >
          {reports.map((report) => (
            <ReportCard
              key={report.slug}
              report={report}
              transformationsSupported={transformationsSupported}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Paper
      elevation={0}
      sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}
    >
      <InsightsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" fontWeight={600} gutterBottom>{t('data.noReportsYet')}</Typography>
      <Typography variant="body2" color="text.secondary">{t('data.reportsWillAppearHereAsTheCatalogGrows')}</Typography>
    </Paper>
  );
}
